import React from 'react';
import { LayoutGrid, Plus } from 'lucide-react';
import { InlineLoadingState } from '../LoadingSpinner';
import type { EmployeeWorkAreaFilter } from '../../utils/employeeWorkAreaFilter';
import { workAreaFilterOptionsForHrAccess } from '../../utils/employeeWorkAreaFilter';

export type EmployeeListItem = {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  color_idx: number;
  user_id: string | null;
  last_seen_at: string | null;
  is_archived: boolean;
};

type EmployeeListRailProps = {
  employees: EmployeeListItem[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  workAreaFilter: EmployeeWorkAreaFilter;
  onWorkAreaFilterChange: (value: EmployeeWorkAreaFilter) => void;
  canAccessInternal?: boolean;
  overviewActive: boolean;
  selectedEmployeeId: string | null;
  activeEmployees: EmployeeListItem[];
  inactiveEmployees: EmployeeListItem[];
  inactiveOpen: boolean;
  onInactiveOpenChange: (open: boolean) => void;
  onOpenOverview: () => void;
  onSelectEmployee: (id: string) => void;
  onNewEmployee: () => void;
  getInitials: (emp: EmployeeListItem) => string;
  formatLastSeenLabel: (lastSeenAt: string | null) => { text: string; isToday: boolean };
  avatarClass: (colorIdx: number) => { bg: string; text: string };
};

export function EmployeeListRail({
  loading,
  search,
  onSearchChange,
  workAreaFilter,
  onWorkAreaFilterChange,
  canAccessInternal = true,
  overviewActive,
  selectedEmployeeId,
  activeEmployees,
  inactiveEmployees,
  inactiveOpen,
  onInactiveOpenChange,
  onOpenOverview,
  onSelectEmployee,
  onNewEmployee,
  getInitials,
  formatLastSeenLabel,
  avatarClass,
}: EmployeeListRailProps) {
  return (
    <aside className="w-[280px] border-r border-app-border flex flex-col min-h-0 bg-app-surface shrink-0">
      <button
        type="button"
        onClick={onOpenOverview}
        className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition-colors ${
          overviewActive ? 'bg-blue-50' : 'bg-app-surface hover:bg-app-hover'
        }`}
      >
        <span
          className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
            overviewActive ? 'bg-pipedrive-blue text-white' : 'bg-blue-50 text-blue-600'
          }`}
        >
          <LayoutGrid className="w-[18px] h-[18px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-app-text">Übersicht · Alle</span>
          <span className="block text-xs text-app-text-subtle">Schichten, Stunden & Genehmigungen</span>
        </span>
      </button>

      <div className="shrink-0 px-3 py-2 bg-app-surface">
        <div
          className="inline-flex w-full rounded-lg border border-app-border bg-app-surface-muted p-0.5 text-xs font-medium"
          role="group"
          aria-label="Arbeitsbereich filtern"
        >
          {workAreaFilterOptionsForHrAccess(canAccessInternal).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onWorkAreaFilterChange(option.value)}
              className={`flex-1 rounded-md px-1.5 py-1.5 transition-colors ${
                workAreaFilter === option.value
                  ? 'bg-app-surface text-app-text shadow-sm'
                  : 'text-app-text-subtle hover:text-app-text-muted'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Suchen…"
          className="mt-2 w-full rounded-md border border-app-border bg-app-surface-muted px-2.5 py-1.5 text-xs outline-none focus:border-blue-400"
        />
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-app-border bg-app-surface px-4 py-1.5">
        <p className="text-xs font-medium text-app-text-muted">
          Aktiv ({activeEmployees.length})
        </p>
        <button
          type="button"
          onClick={onNewEmployee}
          className="inline-flex h-7 items-center gap-1 rounded-md bg-pipedrive-green px-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#218838]"
        >
          <Plus className="w-3.5 h-3.5" />
          Neu
        </button>
      </div>

      <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-2 pt-1">
        {loading && <InlineLoadingState label="Lade…" className="justify-start px-2 py-3" />}
        {!loading && activeEmployees.length === 0 && (
          <p className="px-2 py-3 text-sm text-app-text-subtle">Keine Mitarbeiter</p>
        )}
        {!loading &&
          activeEmployees.map((emp) => {
            const active = !overviewActive && emp.id === selectedEmployeeId;
            const col = avatarClass(emp.color_idx);
            const lastSeen = emp.user_id ? formatLastSeenLabel(emp.last_seen_at) : { text: 'Kein Zugang', isToday: false };
            return (
              <button
                key={emp.id}
                type="button"
                onClick={() => onSelectEmployee(emp.id)}
                className={`w-full mt-1 rounded-md px-2 py-2 text-left flex items-center gap-2 ${
                  active ? 'bg-blue-50' : 'hover:bg-app-hover'
                }`}
              >
                <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0 ${col.bg} ${col.text}`}>
                  {getInitials(emp)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-app-text truncate">
                    {[emp.first_name, emp.last_name].filter(Boolean).join(' ') || 'Unbenannt'}
                  </span>
                  <span className="block text-xs truncate">
                    {emp.position && <span className="text-app-text-subtle">{emp.position} · </span>}
                    <span className={lastSeen.isToday ? 'text-blue-600' : 'text-app-text-subtle'}>{lastSeen.text}</span>
                  </span>
                </span>
              </button>
            );
          })}

        {!loading && inactiveEmployees.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => onInactiveOpenChange(!inactiveOpen)}
              className="sticky top-0 z-10 mt-3 -mx-2 px-2 py-1.5 w-full rounded-md text-left text-xs font-medium text-app-text-muted hover:bg-app-hover flex items-center justify-between bg-app-surface"
            >
              <span>Inaktiv ({inactiveEmployees.length})</span>
              <span>{inactiveOpen ? '−' : '+'}</span>
            </button>
            {inactiveOpen && (
              <div className="mt-1">
                {inactiveEmployees.map((emp) => {
                  const active = !overviewActive && emp.id === selectedEmployeeId;
                  const col = avatarClass(emp.color_idx);
                  const lastSeen = emp.user_id
                    ? formatLastSeenLabel(emp.last_seen_at)
                    : { text: 'Kein Zugang', isToday: false };
                  return (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => onSelectEmployee(emp.id)}
                      className={`w-full mt-1 rounded-md px-2 py-2 text-left flex items-center gap-2 ${
                        active ? 'bg-blue-50' : 'hover:bg-app-hover'
                      }`}
                    >
                      <span
                        className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0 ${col.bg} ${col.text}`}
                      >
                        {getInitials(emp)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-app-text truncate">
                          {[emp.first_name, emp.last_name].filter(Boolean).join(' ') || 'Unbenannt'}
                        </span>
                        <span className="block text-xs truncate">
                          {emp.position && <span className="text-app-text-subtle">{emp.position} · </span>}
                          <span className={lastSeen.isToday ? 'text-blue-600' : 'text-app-text-subtle'}>{lastSeen.text}</span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
